/**
 * Token customizer (snippets/token-customizer.liquid).
 * Boots every [data-mtm] root on the page; each instance reads its own
 * config, strings and DOM from inside its root, so the customizer can be
 * rendered on the token maker page and inside product pages alike.
 */
(function () {
  'use strict';

  /* ================================================================
   *  FRAME LAYOUTS
   *  Text positions are percentages of the 768 x 1063 card. The frame
   *  image URLs come from the tiles' data-mtm-frame-url (Liquid asset_url).
   * ================================================================ */
  var FRAME_LAYOUTS = {
    branch: {
      creatureCoords: { x: 49.84, y: 10.91 },
      ptCoords: { x: 49.52, y: 90.23 },
      abilitiesCoords: { x: 49.84, y: 83.41 },
      fontSizes: { creature: 4.5, pt: 4.3, abilities: 4.0 },
      font: 'Fondamento',
      fontColor: 'white',
    },
    fantasy: {
      creatureCoords: { x: 49.84, y: 9.09 },
      ptCoords: { x: 50.16, y: 93.5 },
      abilitiesCoords: { x: 49.84, y: 86.82 },
      fontSizes: { creature: 4.5, pt: 4.3, abilities: 4.0 },
      font: 'Shippori Mincho',
      fontColor: 'white',
    },
    goth: {
      creatureCoords: { x: 49.84, y: 7.73 },
      ptCoords: { x: 49.84, y: 94 },
      abilitiesCoords: { x: 49.84, y: 87.27 },
      fontSizes: { creature: 4.5, pt: 4.0, abilities: 4.0 },
      font: 'Fondamento',
      fontColor: 'white',
    },
    graffite: {
      creatureCoords: { x: 49.84, y: 10.91 },
      ptCoords: { x: 81.59, y: 92 },
      abilitiesCoords: { x: 7.3, y: 85.68 },
      fontSizes: { creature: 4.6, pt: 5.0, abilities: 4.2 },
      font: 'Fondamento',
      fontColor: 'white',
      abilitiesAlign: 'left',
    },
    paper: {
      creatureCoords: { x: 49.84, y: 9.09 },
      ptCoords: { x: 48.57, y: 94.09 },
      abilitiesCoords: { x: 49.84, y: 86.36 },
      fontSizes: { creature: 4.5, pt: 4.5, abilities: 4.0 },
      font: 'Kaushan Script',
      fontColor: '#504b51',
    },
  };

  /* Print spec: the token is 63 x 88 mm trimmed. 300 dpi needs 744 x 1039 px;
     150 dpi is the floor for an acceptable print. The 768 x 1063 canvas is
     the full-bleed area around it (2 mm bleed per edge), so crops use the
     canvas ratio. */
  var CARD_MM = { w: 63, h: 88 };
  var CARD_PX = { w: 768, h: 1063 };
  var SHARP_PX = 744;
  var SOFT_PX = 372;
  var MIN_SOURCE = { short: 768, long: 1063 };

  /* Product types the automatic bulk discount counts (same list as the
     product page nudge in sections/product.liquid). */
  var DISCOUNT_TYPES = ['MTG Token', 'Customizable Token', 'Customizable Token (Poker Size)'];

  /* ================================================================
   *  SHARED HELPERS
   * ================================================================ */
  var imageCache = {};

  function loadImage(src) {
    if (imageCache[src]) return Promise.resolve(imageCache[src]);
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        imageCache[src] = img;
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  /* Never let a slow or stuck font load block the preview: iOS Safari can
     leave document.fonts.ready pending, which used to freeze every later
     draw (including the one for a freshly picked photo). */
  function fontsReady() {
    if (!document.fonts || !document.fonts.ready) return Promise.resolve();
    return Promise.race([
      document.fonts.ready,
      new Promise(function (resolve) { setTimeout(resolve, 1500); }),
    ]);
  }

  function isValidImage(file) {
    var validMimes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!/\.(jpe?g|png)$/i.test(file.name)) return false;
    /* Some mobile file pickers (Android content:// providers, some cloud-
       storage apps) hand back a File with an empty `type`. Trust the
       extension in that case rather than rejecting a perfectly good photo. */
    if (!file.type) return true;
    return validMimes.indexOf(file.type) !== -1;
  }

  /* <dialog>.showModal() is supported everywhere we target, but guard anyway:
     fall back to the `open` attribute (non-modal, no backdrop). */
  function openDialog(dialog) {
    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return;
      } catch (err) { /* already open */ }
    }
    dialog.setAttribute('open', '');
  }

  function closeDialog(dialog) {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function fill(template, tokens) {
    return Object.keys(tokens).reduce(function (out, key) {
      return out.split('__' + key + '__').join(tokens[key]);
    }, template || '');
  }

  /* Grade a crop by its width in source pixels. */
  function gradeCrop(width) {
    if (width >= SHARP_PX) return 'sharp';
    if (width >= SOFT_PX) return 'soft';
    return 'low';
  }

  function dpiFor(width) {
    return Math.round(width / (CARD_MM.w / 25.4));
  }

  /* ================================================================
   *  CUSTOMIZER INSTANCE
   * ================================================================ */
  function createCustomizer(root) {
    var CONFIG, I18N;
    try {
      CONFIG = JSON.parse(root.querySelector('[data-mtm-config]').textContent);
      I18N = JSON.parse(root.querySelector('[data-mtm-i18n]').textContent);
    } catch (e) {
      console.error('[MTM] Could not parse config', e);
      return;
    }

    /* "quantity:percent" pairs from theme settings → Bulk pricing */
    function parseTiers(str) {
      return String(str || '').split(',')
        .map(function (pair) {
          var kv = pair.split(':');
          return { quantity: parseInt(kv[0], 10), percent: parseInt(kv[1], 10) };
        })
        .filter(function (t) { return t.quantity > 0 && t.percent > 0; })
        .sort(function (a, b) { return a.quantity - b.quantity; });
    }

    var tiers = parseTiers(CONFIG.tiers);
    var tiersDouble = parseTiers(CONFIG.tiersDouble);
    var doubleHandles = String(CONFIG.doubleHandles || '').split(',')
      .map(function (h) { return h.trim(); })
      .filter(Boolean);
    /* The front + back product sits on the deeper double-sided ladder */
    var frontBackIsDouble = !!CONFIG.frontBackHandle && doubleHandles.indexOf(CONFIG.frontBackHandle) !== -1;

    var state = {
      side: 'front',
      front: null, // { file, canvas, width, height }
      back: null,
      frame: null, // { id, url, layout }
      text: { creature: '', pt: '', abilities: '' },
      quantity: 1,
      agreed: false,
      adjust: false,
      uploading: false,
      cartOthers: 0,
      cartDoubles: 0,
      isDrawing: false,
      redrawPending: false,
    };

    var cropper = null;
    var cropSide = null;
    var cropUrl = null;

    function q(sel) { return root.querySelector(sel); }
    function qa(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    var els = {
      sides: qa('[data-mtm-side]'),
      flipper: q('[data-mtm-flipper]'),
      backHint: q('[data-mtm-back-hint]'),
      guides: q('[data-mtm-guides]'),
      faces: { front: q('[data-mtm-face="front"]'), back: q('[data-mtm-face="back"]') },
      canvases: { front: q('[data-mtm-canvas="front"]'), back: q('[data-mtm-canvas="back"]') },
      actions: { front: q('[data-mtm-actions="front"]'), back: q('[data-mtm-actions="back"]') },
      files: { front: q('[data-mtm-file="front"]'), back: q('[data-mtm-file="back"]') },
      status: q('[data-mtm-status]'),
      frames: qa('[data-mtm-frame]'),
      framesMore: q('[data-mtm-frames-more]'),
      textInputs: qa('[data-mtm-text]'),
      textHint: q('[data-mtm-text-hint]'),
      count: q('[data-mtm-count]'),
      printRadios: qa('input[name="mtm-print"]'),
      adjustRadio: q('[data-mtm-adjust]'),
      adjustNotes: q('[data-mtm-adjust-notes]'),
      nudge: q('[data-mtm-nudge]'),
      nudgeText: q('[data-mtm-nudge-text]'),
      nudgeTrack: q('[data-mtm-nudge-track]'),
      nudgeBar: q('[data-mtm-nudge-bar]'),
      tierLists: { main: q('[data-mtm-tiers="main"]'), double: q('[data-mtm-tiers="double"]') },
      agree: q('[data-mtm-agree]'),
      error: q('[data-mtm-error]'),
      readyHint: q('[data-mtm-ready-hint]'),
      sumTokens: q('[data-mtm-sum-tokens]'),
      sumSubtotal: q('[data-mtm-sum-subtotal]'),
      sumDiscount: q('[data-mtm-sum-discount]'),
      sumDiscountLabel: q('[data-mtm-sum-discount-label]'),
      sumDiscountAmount: q('[data-mtm-sum-discount-amount]'),
      sumFee: q('[data-mtm-sum-fee]'),
      qtyInput: q('[data-mtm-qty-input]'),
      qtyBtns: qa('[data-mtm-qty]'),
      add: q('[data-mtm-add]'),
      crop: q('[data-mtm-crop]'),
      cropImage: q('[data-mtm-crop-image]'),
      cropConfirm: q('[data-mtm-crop-confirm]'),
      cropClose: q('[data-mtm-crop-close]'),
      cropReadout: q('[data-mtm-crop-readout]'),
      rotateBtns: qa('[data-mtm-rotate]'),
      enlarge: q('[data-mtm-enlarge]'),
      enlargeImage: q('[data-mtm-enlarge-image]'),
      enlargeClose: q('[data-mtm-enlarge-close]'),
    };

    /* ── Money ── */
    function formatMoney(cents) {
      if (window.TCBMoney) return TCBMoney.formatCents(cents);
      return (CONFIG.currency ? CONFIG.currency + ' ' : '') + (cents / 100).toFixed(2);
    }

    /* ================================================================
     *  SIDES
     * ================================================================ */
    function showSide(side) {
      state.side = side;
      els.sides.forEach(function (btn) {
        var active = btn.dataset.mtmSide === side;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      /* Turn the card over; the face on the far side is inert so it can't
         be clicked or tabbed into through the flip. */
      els.flipper.classList.toggle('is-flipped', side === 'back');
      ['front', 'back'].forEach(function (s) {
        els.faces[s].inert = s !== side;
        els.actions[s].hidden = s !== side;
      });
      updateStatus();
    }

    function syncSideActions(side) {
      var hasImage = !!state[side];
      els.faces[side].classList.toggle('is-empty', !hasImage);
      [els.faces[side], els.actions[side]].forEach(function (scope) {
        scope.querySelectorAll('[data-mtm-when-image]').forEach(function (el) { el.hidden = !hasImage; });
        scope.querySelectorAll('[data-mtm-when-empty]').forEach(function (el) { el.hidden = hasImage; });
      });
      if (side === 'back' && els.backHint) els.backHint.hidden = hasImage;
    }

    function flagFace(side) {
      var face = els.faces[side];
      face.classList.remove('is-attention');
      /* Restart the animation when flagged twice in a row */
      void face.offsetWidth;
      face.classList.add('is-attention');
      setTimeout(function () { face.classList.remove('is-attention'); }, 2000);
    }

    /* ================================================================
     *  STATUS (resolution check / file errors)
     * ================================================================ */
    function setStatus(kind, message) {
      els.status.className = 'mtm__status' + (kind ? ' is-' + kind : '');
      els.status.textContent = message;
    }

    function updateStatus() {
      var art = state[state.side] || state.front;
      if (!art) {
        setStatus('', I18N.resHint);
        return;
      }
      var grade = gradeCrop(art.width);
      var template = grade === 'sharp' ? I18N.resSharp : grade === 'soft' ? I18N.resSoft : I18N.resLow;
      setStatus(grade, fill(template, { W: art.width, H: art.height, DPI: dpiFor(art.width) }));
    }

    /* ================================================================
     *  UPLOAD & CROP
     * ================================================================ */
    function pickFile(side) {
      els.files[side].click();
    }

    function handleFile(file, side) {
      if (!file) return;

      if (!isValidImage(file)) {
        setStatus('low', I18N.errType);
        return;
      }

      /* A blob: URL instead of FileReader's data: URL: a 12-48 MP phone photo
         as a base64 string can exceed iOS Safari's memory budget and fail
         silently. */
      var url = URL.createObjectURL(file);
      var img = new Image();
      var settled = false;
      img.onerror = function () {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(url);
        setStatus('low', I18N.errOpen);
      };
      img.onload = function () {
        if (settled) return;
        settled = true;
        var shortSide = Math.min(img.naturalWidth, img.naturalHeight);
        var longSide = Math.max(img.naturalWidth, img.naturalHeight);
        /* Either orientation is fine: landscape photos can be rotated */
        if (shortSide < MIN_SOURCE.short || longSide < MIN_SOURCE.long) {
          URL.revokeObjectURL(url);
          setStatus('low', I18N.errSize);
          return;
        }
        openCrop(url, side, file);
      };
      img.src = url;
    }

    function openCrop(url, side, file) {
      if (cropUrl && cropUrl !== url) URL.revokeObjectURL(cropUrl);
      cropUrl = url;
      cropSide = { side: side, file: file };
      els.cropConfirm.disabled = true;
      els.cropReadout.className = 'mtm__crop-readout';
      els.cropReadout.textContent = I18N.cropLoading;

      function startCropper() {
        if (cropper) cropper.destroy();
        cropper = new Cropper(els.cropImage, {
          aspectRatio: CARD_PX.w / CARD_PX.h,
          viewMode: 1,
          autoCropArea: 0.9,
          movable: false,
          rotatable: true,
          scalable: false,
          zoomable: false,
          zoomOnWheel: false,
          guides: false,
          center: true,
          responsive: true,
          crop: function (ev) { updateCropReadout(ev.detail); },
          ready: function () {
            els.cropConfirm.disabled = false;
            addCropGuides();
            updateCropReadout(cropper.getData(true));
          },
        });
      }

      /* Attach handlers before src, and check .complete afterwards: some
         mobile engines resolve an in-memory blob: URL before a handler set
         later would see it. decode() makes Cropper measure a fully-decoded
         bitmap (avoids 0-size reads on iOS Safari for large photos). */
      var settled = false;
      function onReady() {
        if (settled) return;
        settled = true;
        if (typeof els.cropImage.decode === 'function') {
          els.cropImage.decode().then(startCropper, startCropper);
        } else {
          startCropper();
        }
      }
      els.cropImage.onerror = function () {
        if (settled) return;
        settled = true;
        els.cropReadout.className = 'mtm__crop-readout is-low';
        els.cropReadout.textContent = I18N.errOpen;
      };
      els.cropImage.onload = onReady;
      els.cropImage.src = url;
      if (els.cropImage.complete && els.cropImage.naturalWidth > 0) onReady();

      openDialog(els.crop);
    }

    function updateCropReadout(d) {
      if (!d) return;
      var w = Math.round(d.width);
      var h = Math.round(d.height);
      var grade = gradeCrop(w);
      var template = grade === 'sharp' ? I18N.resSharp : grade === 'soft' ? I18N.resSoft : I18N.resLow;
      els.cropReadout.className = 'mtm__crop-readout is-' + grade;
      els.cropReadout.textContent = fill(template, { W: w, H: h, DPI: dpiFor(w) });
    }

    function addCropGuides() {
      var box = els.crop.querySelector('.cropper-crop-box');
      if (!box || box.querySelector('.mtm__crop-cut')) return;
      ['mtm__crop-cut', 'mtm__crop-safe'].forEach(function (cls) {
        var g = document.createElement('div');
        g.className = cls;
        box.appendChild(g);
      });
    }

    /* Rotate, then fit the rotated image back into the crop area and reset
       the crop box to the largest card-shaped area that fits. */
    function rotate(deg) {
      if (!cropper) return;
      cropper.rotate(deg);
      var container = cropper.getContainerData();
      var canvas = cropper.getCanvasData();
      var scale = Math.min(container.width / canvas.width, container.height / canvas.height);
      var w = canvas.width * scale;
      var h = canvas.height * scale;
      cropper.setCanvasData({ left: (container.width - w) / 2, top: (container.height - h) / 2, width: w, height: h });
      var fitted = cropper.getCanvasData();
      var ratio = CARD_PX.w / CARD_PX.h;
      var boxW = Math.min(fitted.width, fitted.height * ratio) * 0.9;
      var boxH = boxW / ratio;
      cropper.setCropBoxData({
        left: fitted.left + (fitted.width - boxW) / 2,
        top: fitted.top + (fitted.height - boxH) / 2,
        width: boxW,
        height: boxH,
      });
    }

    function confirmCrop() {
      if (!cropper || !cropSide) return;
      var data = cropper.getData(true);
      /* getCroppedCanvas applies the rotation and draws straight into a
         768 x 1063 canvas, so huge photos never need a full-size canvas
         (iOS caps canvases at ~16.7 MP). */
      var canvas = cropper.getCroppedCanvas({
        width: CARD_PX.w,
        height: CARD_PX.h,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      if (!canvas) return;

      var side = cropSide.side;
      state[side] = { file: cropSide.file, canvas: canvas, width: data.width, height: data.height };
      closeCrop();

      syncSideActions(side);
      if (side === 'front') drawFront();
      else drawBack();
      updateStatus();
      updatePricing();
      updateReady();
      clearError();
    }

    function closeCrop() {
      if (cropper) {
        cropper.destroy();
        cropper = null;
      }
      if (cropUrl) {
        URL.revokeObjectURL(cropUrl);
        cropUrl = null;
      }
      cropSide = null;
      closeDialog(els.crop);
    }

    function adjustCrop(side) {
      var art = state[side];
      if (!art) return pickFile(side);
      openCrop(URL.createObjectURL(art.file), side, art.file);
    }

    function removeArt(side) {
      state[side] = null;
      syncSideActions(side);
      if (side === 'front') drawFront();
      else drawBack();
      updateStatus();
      updatePricing();
      updateReady();
    }

    function enlarge(side) {
      els.enlargeImage.src = els.canvases[side].toDataURL('image/jpeg', 0.92);
      openDialog(els.enlarge);
    }

    /* ================================================================
     *  FRAMES & TEXT
     * ================================================================ */
    function selectFrame(btn) {
      var id = btn.dataset.mtmFrame;
      state.frame = id && FRAME_LAYOUTS[id]
        ? { id: id, url: btn.dataset.mtmFrameUrl, layout: FRAME_LAYOUTS[id] }
        : null;
      els.frames.forEach(function (el) {
        var active = el === btn;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      syncTextHint();
      drawFront();
    }

    function showAllFrames() {
      els.frames.forEach(function (el) { el.hidden = false; });
      els.framesMore.hidden = true;
      els.framesMore.setAttribute('aria-expanded', 'true');
    }

    var drawTimeout = null;

    function handleText() {
      els.textInputs.forEach(function (input) {
        state.text[input.dataset.mtmText] = input.value.trim();
        if (input.dataset.mtmText === 'abilities' && els.count) {
          els.count.textContent = input.value.length + '/' + input.maxLength;
        }
      });
      syncTextHint();
      clearTimeout(drawTimeout);
      drawTimeout = setTimeout(drawFront, 50);
    }

    /* Text positions come from the frame, so with no frame there's nowhere
       to draw it: say so instead of silently ignoring the typing. */
    function syncTextHint() {
      var hasText = !!(state.text.creature || state.text.pt || state.text.abilities);
      els.textHint.hidden = !(hasText && !state.frame);
    }

    /* ================================================================
     *  CANVAS RENDERING
     * ================================================================ */
    function drawFront() {
      /* A draw already in flight: draw again when it finishes instead of
         dropping this request. */
      if (state.isDrawing) {
        state.redrawPending = true;
        return;
      }
      state.isDrawing = true;
      state.redrawPending = false;

      var canvas = els.canvases.front;
      var ctx = canvas.getContext('2d');
      var w = canvas.width;
      var h = canvas.height;
      var frame = state.frame;

      /* No upload yet: leave the canvas clear so the face's upload prompt
         shows through, with the frame and text drawn over it if picked. */
      var chain = fontsReady()
        .then(function () {
          ctx.clearRect(0, 0, w, h);
          if (state.front) ctx.drawImage(state.front.canvas, 0, 0, w, h);
          if (!frame) return;
          return loadImage(frame.url).then(function (frameImg) {
            ctx.drawImage(frameImg, 0, 0, w, h);
            drawText(ctx, frame.layout, w, h);
          });
        });

      chain
        .catch(function (err) { console.error('[MTM] Draw error:', err); })
        .finally(function () {
          state.isDrawing = false;
          if (state.redrawPending) drawFront();
        });
    }

    function drawText(ctx, layout, w, h) {
      ctx.save();
      ctx.fillStyle = layout.fontColor || 'white';
      ctx.textAlign = 'center';

      if (state.text.creature) {
        ctx.font = (h * layout.fontSizes.creature) / 100 + 'px ' + layout.font;
        ctx.fillText(state.text.creature, (layout.creatureCoords.x / 100) * w, (layout.creatureCoords.y / 100) * h);
      }
      if (state.text.pt) {
        ctx.font = (h * layout.fontSizes.pt) / 100 + 'px ' + layout.font;
        ctx.fillText(state.text.pt, (layout.ptCoords.x / 100) * w, (layout.ptCoords.y / 100) * h);
      }
      if (state.text.abilities) {
        var align = layout.abilitiesAlign || 'center';
        var x = (layout.abilitiesCoords.x / 100) * w;
        var maxWidth = align === 'left' ? w - 2 * x : w * 0.8;
        var fit = fitAbilities(ctx, state.text.abilities, layout, h, maxWidth);
        ctx.font = fit.size + 'px ' + layout.font;
        ctx.textAlign = align;
        ctx.fillStyle = 'white';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        /* Lines are centred on the frame's abilities baseline */
        var lineHeight = fit.size * 1.15;
        var y = (layout.abilitiesCoords.y / 100) * h - ((fit.lines.length - 1) * lineHeight) / 2;
        fit.lines.forEach(function (line, i) {
          ctx.fillText(line, x, y + i * lineHeight);
        });
      }
      ctx.restore();
    }

    /* Wrap abilities text to the frame's text box, shrinking the font (to
       60% of the frame's size at most) until it fits in three lines. */
    function fitAbilities(ctx, text, layout, h, maxWidth) {
      var base = (h * layout.fontSizes.abilities) / 100;
      var words = text.replace(/\s+/g, ' ').split(' ');
      var size = base;
      var lines;
      for (;;) {
        ctx.font = size + 'px ' + layout.font;
        lines = wrapWords(ctx, words, maxWidth);
        if (lines.length <= 3 || size <= base * 0.6) break;
        size *= 0.9;
      }
      return { size: size, lines: lines.slice(0, 3) };
    }

    function wrapWords(ctx, words, maxWidth) {
      var lines = [];
      var line = '';
      words.forEach(function (word) {
        var next = line ? line + ' ' + word : word;
        if (line && ctx.measureText(next).width > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = next;
        }
      });
      if (line) lines.push(line);
      return lines;
    }

    function drawBack() {
      var canvas = els.canvases.back;
      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (state.back) ctx.drawImage(state.back.canvas, 0, 0, canvas.width, canvas.height);
    }

    /* ================================================================
     *  PRICING: unit price, bulk-discount progress and total
     * ================================================================ */
    function hasCustomBack() {
      return !!state.back && !!CONFIG.variantIdFrontBack;
    }

    function unitPrice() {
      return hasCustomBack() && CONFIG.priceFrontBack ? CONFIG.priceFrontBack : CONFIG.price;
    }

    /* Every add from the customizer is its own cart line (unique design id),
       so everything eligible already in the cart counts towards the tier. */
    function refreshCartCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = 0;
          var doubles = 0;
          (cart.items || []).forEach(function (item) {
            if (DISCOUNT_TYPES.indexOf(item.product_type) === -1) return;
            count += item.quantity;
            if (doubleHandles.indexOf(item.handle) !== -1) doubles += item.quantity;
          });
          state.cartOthers = count;
          state.cartDoubles = doubles;
          updatePricing();
        })
        .catch(function () {});
    }

    /* Highest tier at or below count, and the first one above it */
    function tierFor(ladder, count) {
      var reached = null;
      var next = null;
      ladder.forEach(function (t) {
        if (count >= t.quantity) reached = t;
        else if (!next) next = t;
      });
      return { reached: reached, next: next };
    }

    function updatePricing() {
      var unit = unitPrice() || 0;
      var qty = state.quantity;

      /* Same rules as the cart drawer: every eligible token counts towards
         the main ladder; double-sided tokens also have their own deeper
         ladder, and Shopify gives a double-sided line the better of the two. */
      var main = tierFor(tiers, state.cartOthers + qty);
      var useDouble = hasCustomBack() && frontBackIsDouble && tiersDouble.length > 0;
      var doubleTier = useDouble ? tierFor(tiersDouble, state.cartDoubles + qty) : null;
      var percent = Math.max(
        main.reached ? main.reached.percent : 0,
        doubleTier && doubleTier.reached ? doubleTier.reached.percent : 0
      );

      var subtotal = unit * qty;
      var discount = Math.round(subtotal * percent / 100);
      var fee = state.adjust && CONFIG.adjustmentPrice ? CONFIG.adjustmentPrice : 0;

      if (!state.uploading) els.add.textContent = fill(I18N.addToCart, { PRICE: formatMoney(subtotal - discount + fee) });

      els.sumTokens.textContent = qty === 1
        ? fill(I18N.tokensOne, { PRICE: formatMoney(unit) })
        : fill(I18N.tokensOther, { COUNT: qty, PRICE: formatMoney(unit) });
      els.sumSubtotal.textContent = formatMoney(subtotal);
      els.sumDiscount.hidden = !discount;
      if (discount) {
        els.sumDiscountLabel.textContent = fill(I18N.discountLine, { PERCENT: percent });
        els.sumDiscountAmount.textContent = '\u2212' + formatMoney(discount);
      }
      if (els.sumFee) els.sumFee.hidden = !fee;

      if (!els.nudge || !tiers.length) return;

      /* Progress and tiles follow the ladder this line is priced on */
      var ladder = useDouble ? doubleTier : main;
      var count = useDouble ? state.cartDoubles + qty : state.cartOthers + qty;
      var strings = useDouble
        ? { add: I18N.ladderDoubleAdd, appliedNext: I18N.ladderDoubleAppliedNext, best: I18N.ladderDoubleBest }
        : { add: I18N.ladderAdd, appliedNext: I18N.ladderAppliedNext, best: I18N.ladderBest };
      var list = useDouble ? els.tierLists.double : els.tierLists.main;

      if (els.tierLists.double) els.tierLists.double.hidden = !useDouble;
      if (els.tierLists.main) els.tierLists.main.hidden = useDouble;
      if (list) {
        list.querySelectorAll('[data-mtm-tier]').forEach(function (el) {
          el.classList.toggle('is-reached', count >= parseInt(el.dataset.mtmTier, 10));
        });
      }

      if (!ladder.next) {
        els.nudgeText.textContent = fill(strings.best, { PERCENT: ladder.reached ? ladder.reached.percent : percent });
        els.nudgeTrack.hidden = true;
        return;
      }

      els.nudgeTrack.hidden = false;
      var progress = Math.min(100, Math.round((count / ladder.next.quantity) * 100));
      els.nudgeBar.style.width = progress + '%';
      els.nudgeTrack.setAttribute('aria-valuenow', String(progress));

      var remaining = ladder.next.quantity - count;
      els.nudgeText.textContent = ladder.reached
        ? fill(strings.appliedNext, { PERCENT: ladder.reached.percent, REMAINING: remaining, NEXT_PERCENT: ladder.next.percent })
        : fill(strings.add, { REMAINING: remaining, PERCENT: ladder.next.percent });
    }

    function setQuantity(value) {
      var n = parseInt(value, 10);
      if (!n || n < 1) n = 1;
      if (n > 999) n = 999;
      state.quantity = n;
      if (String(els.qtyInput.value) !== String(n)) els.qtyInput.value = n;
      updatePricing();
    }

    /* ================================================================
     *  ERRORS
     * ================================================================ */
    function showError(msg) {
      els.error.textContent = msg;
      els.error.hidden = false;
    }

    function clearError() {
      els.error.hidden = true;
      els.error.textContent = '';
      els.agree.closest('.mtm__agree').classList.remove('is-invalid');
    }

    /* The button stays clickable while something is missing (a tap points
       at it); it just looks inactive and the hint below says what's left. */
    function updateReady() {
      var needFront = !state.front;
      var needAgree = !state.agreed;
      var ready = !needFront && !needAgree;
      els.add.classList.toggle('is-inactive', !ready);
      els.add.setAttribute('aria-disabled', ready ? 'false' : 'true');
      els.readyHint.hidden = ready;
      els.readyHint.textContent = needFront && needAgree ? I18N.needBoth : needFront ? I18N.needFront : I18N.needAgree;
    }

    /* ================================================================
     *  ADD TO CART
     *  Flow: stage → direct upload to object storage → register metaobject
     *  → cart add (Shopify Ajax Cart API)
     * ================================================================ */
    function canvasToJpegBlob(canvas) {
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.85);
      });
    }

    function setBusy(busy) {
      state.uploading = busy;
      els.add.disabled = busy;
      if (busy) {
        els.add.innerHTML = '<span class="mtm__loading-dots"><span></span><span></span><span></span></span>';
      } else {
        updatePricing();
      }
    }

    function handleAddToCart() {
      if (state.uploading) return;
      clearError();

      if (!state.front || !state.agreed) {
        if (!state.agreed) els.agree.closest('.mtm__agree').classList.add('is-invalid');
        if (!state.front) {
          showSide('front');
          flagFace('front');
        } else {
          els.agree.focus();
        }
        return;
      }

      setBusy(true);

      var uploadEndpoint = CONFIG.uploadEndpoint;
      var authHeaders = { 'Content-Type': 'application/json' };
      if (CONFIG.apiToken) authHeaders['Authorization'] = 'Bearer ' + CONFIG.apiToken;
      var withBack = hasCustomBack();
      var cartVariantId = withBack ? CONFIG.variantIdFrontBack : CONFIG.variantId;
      var sides = withBack ? ['front', 'back'] : ['front'];
      var stageData;

      /* DO Spaces needs the ACL header sent for public-read to apply;
         R2 presigned URLs reject unsigned x-amz-* headers, so send it only for Spaces. */
      function imagePutHeaders(presignedUrl) {
        var headers = { 'Content-Type': 'image/jpeg' };
        if (/\.digitaloceanspaces\.com$/.test(new URL(presignedUrl).hostname)) {
          headers['x-amz-acl'] = 'public-read';
        }
        return headers;
      }

      /* Fail fast instead of spinning forever if the upload endpoint hangs */
      function requestTimeout() {
        return typeof AbortSignal !== 'undefined' && AbortSignal.timeout
          ? AbortSignal.timeout(30000)
          : undefined;
      }

      function putSide(side) {
        return canvasToJpegBlob(els.canvases[side]).then(function (blob) {
          var target = stageData.uploads[side].presignedUrl;
          return fetch(target, { method: 'PUT', headers: imagePutHeaders(target), body: blob })
            .then(function (res) {
              if (!res.ok) throw new Error(side + ' upload failed: ' + res.status);
            });
        });
      }

      /* 1. Stage: get presigned URLs */
      fetch(uploadEndpoint + '?action=stage', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ sides: sides }),
        signal: requestTimeout(),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Stage request failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          stageData = data;
          /* 2. Upload the rendered card faces */
          return Promise.all(sides.map(putSide));
        })
        .then(function () {
          /* 3. Register: create the Shopify metaobject */
          var registerBody = {
            designId: stageData.designId,
            frontImageUrl: stageData.uploads.front.cdnUrl,
            creature: state.text.creature || undefined,
            powerToughness: state.text.pt || undefined,
            abilities: state.text.abilities || undefined,
            frame: state.frame ? state.frame.id : undefined,
            isDoubleSided: withBack,
          };
          if (withBack) registerBody.backImageUrl = stageData.uploads.back.cdnUrl;

          return fetch(uploadEndpoint + '?action=register', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify(registerBody),
            signal: requestTimeout(),
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('Register request failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          /* 4. Add to cart with line item properties */
          var properties = {
            '_design_id': stageData.designId,
            '_front_image': stageData.uploads.front.cdnUrl,
            '_is_double_sided': withBack ? 'true' : 'false',
          };
          if (withBack) properties['_back_image'] = stageData.uploads.back.cdnUrl;

          /* Visible properties for order confirmation emails */
          if (state.text.creature) properties['Creature'] = state.text.creature;
          if (state.text.pt) properties['Power/Toughness'] = state.text.pt;
          if (state.text.abilities) properties['Abilities'] = state.text.abilities;
          if (state.frame) properties['Frame'] = state.frame.id;
          if (state.adjust) {
            properties['Manual adjustment'] = 'Yes';
            var notes = els.adjustNotes ? els.adjustNotes.querySelector('textarea').value.trim() : '';
            if (notes) properties['Adjustment notes'] = notes;
          }

          if (window.TCBTrackdesk) {
            var cid = TCBTrackdesk.getClickId();
            if (cid) properties._trackdeskClickId = cid;
          }

          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: cartVariantId, quantity: state.quantity, properties: properties }),
          });
        })
        .then(function (res) { return res.json(); })
        .then(function (cartData) {
          if (cartData.status && cartData.status >= 400) {
            throw new Error(cartData.description || 'Cart error');
          }
          /* 5. Optional manual-adjustment fee: its own cart line */
          if (state.adjust && CONFIG.adjustmentVariantId) {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: CONFIG.adjustmentVariantId,
                quantity: 1,
                properties: { '_for': 'Magic Token Maker' },
              }),
            }).then(function (res) { return res.json(); });
          }
        })
        .then(function () {
          refreshCartCount();
          if (window.CartDrawer) CartDrawer.open();
          else window.location.href = '/cart';
        })
        .catch(function (err) {
          if (err && err.name === 'TimeoutError') {
            console.error('[MTM] Request timed out after 30s — upload endpoint (' + uploadEndpoint + ') is not responding');
          } else {
            console.error('[MTM] Error adding to cart:', err);
          }
          showError(I18N.errCart);
        })
        .finally(function () {
          setBusy(false);
        });
    }

    /* ================================================================
     *  EVENTS
     * ================================================================ */
    function bindEvents() {
      els.sides.forEach(function (btn) {
        btn.addEventListener('click', function () { showSide(btn.dataset.mtmSide); });
      });

      ['front', 'back'].forEach(function (side) {
        /* Card click (incl. its "Choose file" button): upload when empty,
           re-crop once there's art */
        var face = els.faces[side];
        face.addEventListener('click', function () {
          if (state[side]) adjustCrop(side);
          else pickFile(side);
        });
        face.addEventListener('dragover', function (e) {
          e.preventDefault();
          face.classList.add('is-dragover');
        });
        face.addEventListener('dragleave', function () { face.classList.remove('is-dragover'); });
        face.addEventListener('drop', function (e) {
          e.preventDefault();
          face.classList.remove('is-dragover');
          handleFile(e.dataTransfer && e.dataTransfer.files[0], side);
        });
        els.files[side].addEventListener('change', function (e) {
          var file = e.target.files && e.target.files[0];
          /* Reset so picking the same photo again still fires "change" */
          e.target.value = '';
          handleFile(file, side);
        });
        els.actions[side].addEventListener('click', function (e) {
          var btn = e.target.closest('[data-mtm-action]');
          if (!btn) return;
          var action = btn.dataset.mtmAction;
          if (action === 'upload') pickFile(side);
          else if (action === 'adjust') adjustCrop(side);
          else if (action === 'enlarge') enlarge(side);
          else if (action === 'remove') removeArt(side);
        });
      });

      els.cropConfirm.addEventListener('click', confirmCrop);
      els.cropClose.addEventListener('click', closeCrop);
      els.crop.addEventListener('cancel', function (e) {
        e.preventDefault();
        closeCrop();
      });
      els.rotateBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { rotate(parseInt(btn.dataset.mtmRotate, 10)); });
      });

      els.enlargeClose.addEventListener('click', function () { closeDialog(els.enlarge); });
      els.enlarge.addEventListener('click', function (e) {
        if (e.target === els.enlarge) closeDialog(els.enlarge);
      });

      els.frames.forEach(function (btn) {
        btn.addEventListener('click', function () { selectFrame(btn); });
      });
      if (els.framesMore) els.framesMore.addEventListener('click', showAllFrames);

      els.textInputs.forEach(function (input) {
        input.addEventListener('input', handleText);
      });

      /* Radios only fire change on the one that becomes checked, so listen
         to the whole group. */
      function syncAdjust() {
        state.adjust = els.adjustRadio ? els.adjustRadio.checked : false;
        if (els.adjustNotes) els.adjustNotes.hidden = !state.adjust;
        updatePricing();
      }
      els.printRadios.forEach(function (radio) { radio.addEventListener('change', syncAdjust); });
      syncAdjust();

      els.agree.addEventListener('change', function () {
        state.agreed = els.agree.checked;
        if (state.agreed) clearError();
        updateReady();
      });

      if (els.guides) {
        els.guides.addEventListener('change', function () {
          root.classList.toggle('mtm--guides', els.guides.checked);
        });
      }

      els.qtyBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          setQuantity(state.quantity + parseInt(btn.dataset.mtmQty, 10));
        });
      });
      els.qtyInput.addEventListener('input', function () {
        if (els.qtyInput.value !== '') setQuantity(els.qtyInput.value);
      });
      els.qtyInput.addEventListener('blur', function () { setQuantity(els.qtyInput.value); });

      els.add.addEventListener('click', handleAddToCart);

      document.addEventListener('cart:updated', refreshCartCount);
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) refreshCartCount();
      });
    }

    /* ================================================================
     *  INIT
     * ================================================================ */
    bindEvents();
    syncSideActions('front');
    syncSideActions('back');
    drawFront();
    drawBack();
    updatePricing();
    updateReady();
    refreshCartCount();
  }

  function boot() {
    document.querySelectorAll('[data-mtm]').forEach(function (root) {
      if (root.dataset.mtmReady) return;
      root.dataset.mtmReady = 'true';
      createCustomizer(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
